/**
 * Single Brand Lift Survey HTML creative.
 *
 * Live preview, placement-size preview, and the trafficked survey file all
 * render this document. Layout is driven by CSS container queries.
 *
 * Interaction:
 * - clickToSubmit true  → radio list + Submit (kept in the template; wizard does not expose the toggle).
 * - clickToSubmit false → pill buttons; choosing an answer submits (current wizard default).
 *
 * Tracking fires window.aderize.trackEvent when present, otherwise console.log.
 * Action strings match Keeper reporting SQL (Exposed / Non-exposed, flight id,
 * Exp:N, ans_{index} and ans_{answer text}, lang_{locale}).
 */

import { buildSurveyPack, surveyLocaleRuntime } from './catalogue.js';
import { BLS_MODE } from './constants.js';
import { fillBrandPlaceholder } from './metrics.js';

const CHECK_SVG =
  '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><path d="M5 11.5l4 4 8-9" stroke="#6A63EC" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function jsonForScript(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function trackingOptionsFromConfig(config = {}) {
  const answers = (config.answerOptions || [])
    .map((answer) => fillBrandPlaceholder(answer, config.brand))
    .filter((answer) => String(answer || '').trim());
  const buckets = config.frequencyTesting?.enabled ? config.frequencyTesting.buckets : null;
  return {
    answers,
    optimized: config.blsMode !== BLS_MODE.STANDARD,
    exposureFrequency:
      buckets?.length ? Number(buckets[0]) || 1 : Number(config.exposureFrequency) || 1,
  };
}

/**
 * Runtime helpers inlined into the survey HTML.
 * Unreplaced $FLIGHT_ID tokens are skipped so the ad server can substitute
 * them in the file. window.aderize (flightId, exposed/blsPath, exp) is
 * preferred when present.
 */
function trackingRuntimeJs(options = {}) {
  const answersJson = jsonForScript(options.answers || []);
  const mode = options.optimized === false ? 'Standard' : 'Optimized';
  const defaultExp = Number(options.exposureFrequency) || 1;
  return `
  var BLS_ANSWERS = ${answersJson};
  var BLS_MODE_LABEL = ${jsonForScript(mode)};
  var BLS_DEFAULT_EXP = ${defaultExp};

  function blsAderize() {
    try { return window.aderize || {}; } catch (e) { return {}; }
  }

  function blsQuery(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }

  function blsFirst() {
    for (var i = 0; i < arguments.length; i += 1) {
      var value = arguments[i];
      if (value == null) continue;
      var text = String(value).trim();
      if (!text || text.charAt(0) === '$') continue;
      return text;
    }
    return '';
  }

  function blsPath() {
    var ader = blsAderize();
    if (typeof ader.exposed === 'boolean') return ader.exposed ? 'Exposed' : 'Non-exposed';
    var raw = blsFirst(
      ader.blsPath, ader.path, ader.exposurePath, ader.arm,
      blsQuery('bls_path'), blsQuery('path'), blsQuery('arm')
    );
    if (/non[- ]?exposed/i.test(raw) || /control/i.test(raw)) return 'Non-exposed';
    return 'Exposed';
  }

  function blsFlightId() {
    var ader = blsAderize();
    return blsFirst(
      ader.flightId, ader.flight_id, ader.lineItemId,
      blsQuery('flight_id'), blsQuery('flightId'),
      '$FLIGHT_ID'
    ) || '$FLIGHT_ID';
  }

  function blsExp() {
    var ader = blsAderize();
    var n = Number(blsFirst(ader.exposureCount, ader.exp, ader.nExposures, blsQuery('exp')));
    return n > 0 ? n : BLS_DEFAULT_EXP;
  }

  function blsMode() {
    var ader = blsAderize();
    if (typeof ader.optimized === 'boolean') return ader.optimized ? 'Optimized' : 'Standard';
    var raw = blsFirst(ader.blsMode, ader.mode, blsQuery('bls_mode'), BLS_MODE_LABEL);
    if (/standard/i.test(raw)) return 'Standard';
    return 'Optimized';
  }

  function blsActionBase() {
    return [blsPath(), blsMode(), blsFlightId(), 'Exp:' + blsExp()].join(' ');
  }

  function blsAnswerSuffix(index) {
    var n = Number(index);
    if (!isFinite(n) || n < 0) return '';
    var text = BLS_ANSWERS[n] != null ? String(BLS_ANSWERS[n]) : '';
    var parts = ['ans_' + (n + 1)];
    if (text) parts.push('ans_' + text);
    return ' ' + parts.join(' ');
  }

  function blsLanguage() {
    var locale = 'en';
    try {
      if (typeof blsLocale === 'function') {
        var resolved = blsLocale();
        if (resolved) locale = String(resolved);
      }
    } catch (e) {}
    return String(locale).replace(/[^A-Za-z0-9-]/g, '') || 'en';
  }

  function blsTrack(category, action) {
    var trackedAction = String(action || '') + ' lang_' + blsLanguage();
    var payload = { category: category, action: trackedAction };
    try {
      if (window.aderize && typeof window.aderize.trackEvent === 'function') {
        window.aderize.trackEvent(payload);
        return;
      }
    } catch (e) {}
    if (typeof console !== 'undefined' && console.log) {
      console.log('[BLS] ' + category + ' — ' + trackedAction);
    }
  }

  window.blsTrackShown = function () {
    blsTrack('Survey shown', blsActionBase());
  };
  window.blsTrackAnswered = function (index) {
    blsTrack('Survey answered', blsActionBase() + blsAnswerSuffix(index));
  };
`;
}

function trackingBootstrapJs(options) {
  return `${trackingRuntimeJs(options)}
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.blsTrackShown(); });
  } else {
    window.blsTrackShown();
  }
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-index]');
    if (!button || window.__blsAnswered) return;
    var index = Number(button.getAttribute('data-index'));
    window.setTimeout(function () {
      if (window.__blsAnswered) return;
      window.__blsAnswered = true;
      window.blsTrackAnswered(index);
    }, 320);
  }, true);
`;
}

export function injectBlsTracking(html, options = {}) {
  const source = String(html || '');
  const script = `<script id="bls-track-events">${trackingBootstrapJs(options)}</script>`;
  if (/id="bls-track-events"/.test(source)) {
    return source.replace(/<script id="bls-track-events">[\s\S]*?<\/script>/, script);
  }
  if (/blsTrackShown/.test(source)) return source;
  if (/<\/body>/i.test(source)) return source.replace(/<\/body>/i, `${script}\n</body>`);
  return `${source}\n${script}`;
}

export function buildTraffickedSurveyHtml(config = {}, uploadedHtml) {
  const tracking = trackingOptionsFromConfig(config);
  if (uploadedHtml) return injectBlsTracking(uploadedHtml, tracking);
  return buildSurveyHtml({
    question: config.questionWording,
    answers: tracking.answers,
    brand: config.brand,
    clickToSubmit: config.clickToSubmit,
    showReset: false,
    optimized: tracking.optimized,
    exposureFrequency: tracking.exposureFrequency,
    metricId: config.brandMetric,
    actionWord: config.questionActionWord,
    answerCount: config.answerCount,
    previewLocale: null,
  });
}

export function surveyHtmlToFile(html, config = {}) {
  const base = [config.name, config.brand].filter(Boolean).join('_') || 'brand_lift_survey';
  const filename = `${String(base).replace(/[^\w.-]+/g, '_')}_survey.html`;
  return new File([html], filename, { type: 'text/html' });
}

function highlightBrand(text, brand) {
  const source = String(text || '');
  const display = brand
    ? source.replace(/\[BRAND\]/gi, brand)
    : source.replace(/\[BRAND\]/gi, '[Brand]');
  const escaped = escapeHtml(display);
  const needle = escapeHtml(brand || '[Brand]');
  return escaped.split(needle).join(`<span class="brand">${needle}</span>`);
}

const SURVEY_CSS = `
:root {
  --bls-accent: #6A63EC;
  --bls-accent-deep: #4B47D6;
  --bls-bg: #EEEDF8;
  --bls-line: #D4D2EE;
  --bls-pill: #C8C6E8;
  --bls-ink: #1A1A2E;
  --bls-muted: #6B7280;
  --bls-radio: #B0ACDC;
}
*, *::before, *::after { box-sizing: border-box; }
html, body {
  margin: 0;
  width: 100%;
  height: 100%;
  background: var(--bls-bg);
  font-family: Inter, system-ui, -apple-system, Segoe UI, sans-serif;
  -webkit-font-smoothing: antialiased;
}
button { font-family: inherit; }
.survey {
  container-type: size;
  width: 100%;
  height: 100%;
  background: var(--bls-bg);
  overflow: hidden;
  position: relative;
}
.layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 20px;
  gap: 0;
}
.copy { min-width: 0; flex-shrink: 0; }
.panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
}
.tagline {
  margin: 0 0 12px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bls-accent);
}
.question {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--bls-ink);
}
.question .brand { color: var(--bls-accent-deep); }
.answers {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
}
.answers.is-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-content: start;
  gap: 8px;
}
.pill {
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid var(--bls-pill);
  background: #fff;
  color: var(--bls-ink);
  border-radius: 12px;
  padding: 12px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;
  transition: border-color .15s, background .15s, color .15s;
}
.pill:hover { border-color: rgba(106,99,236,.6); background: #F4F3FB; }
.pill.is-on { border-color: var(--bls-accent); background: #E8E7FB; color: var(--bls-accent-deep); }
.pill.is-wide { grid-column: 1 / -1; }
.choice {
  -webkit-appearance: none;
  appearance: none;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  text-align: start;
  cursor: pointer;
  color: #2D2D3E;
  font-size: 14px;
  line-height: 1.3;
}
.choice.is-on { color: var(--bls-ink); font-weight: 600; }
.dot {
  width: 20px;
  height: 20px;
  aspect-ratio: 1 / 1;
  box-sizing: border-box;
  border: 2px solid var(--bls-radio);
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color .15s, background .15s;
}
.choice.is-on .dot { border-color: var(--bls-accent); background: var(--bls-accent); }
.dot::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
  transform: scale(0);
  transition: transform .15s;
}
.choice.is-on .dot::after { transform: scale(1); }
.rule {
  border: 0;
  border-top: 1px solid var(--bls-line);
  width: 100%;
  margin: 0 0 16px;
}
.submit {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  margin-top: auto;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--bls-pill);
  background: transparent;
  color: #9CA3AF;
  font-size: 14px;
  font-weight: 500;
  cursor: not-allowed;
  transition: border-color .15s, color .15s, background .15s;
}
.submit.is-ready {
  border-color: rgba(106,99,236,.6);
  color: var(--bls-accent);
  cursor: pointer;
}
.submit.is-ready:hover { background: #E8E7FB; }
.thanks {
  display: none;
  position: absolute;
  inset: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  text-align: center;
  background: var(--bls-bg);
}
.thanks.is-in { display: flex; animation: bls-in .38s ease; }
.thanks-mark {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(106,99,236,.14);
  display: flex;
  align-items: center;
  justify-content: center;
}
.thanks h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--bls-ink);
}
.thanks p {
  margin: 0;
  font-size: 12px;
  color: var(--bls-muted);
}
.reset {
  -webkit-appearance: none;
  appearance: none;
  margin-top: 4px;
  border: 0;
  background: none;
  color: var(--bls-accent);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.layout.is-out { animation: bls-out .22s ease forwards; }
@keyframes bls-in {
  from { opacity: 0; transform: scale(.96); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes bls-out {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-8px); }
}

/* Medium rectangle (300×250): ≤3 answers stack; >3 pack to fit. */
@container (max-height: 280px) and (max-width: 420px) {
  .layout { padding: 14px 16px; }
  .tagline { margin-bottom: 8px; }
  .question { font-size: 13px; margin-bottom: 10px; line-height: 1.3; }
  .rule { margin: 0 0 10px; }
  .answers { gap: 8px; }
  .answers.is-pills .pill { padding: 8px 8px; font-size: 11px; }
  .choice { font-size: 12px; gap: 8px; }
  .dot { width: 16px; height: 16px; }
  .dot::after { width: 6px; height: 6px; }
  .submit { padding: 8px; font-size: 12px; margin-top: 6px; }
  .answers.is-few {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    grid-template-columns: none;
    gap: 8px;
  }
  .answers.is-few .pill,
  .answers.is-few .choice { width: 100%; }
  .answers.is-few .pill.is-wide { grid-column: auto; }
  .answers.is-pills.is-fit {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    align-content: start;
  }
  .answers.is-pills.is-fit .pill { padding: 6px 6px; font-size: 10px; }
  .answers.is-radios.is-fit {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 8px;
    align-content: start;
  }
  .answers.is-radios.is-fit .choice { width: auto; font-size: 11px; gap: 6px; }
  .answers.is-radios.is-fit .choice.is-wide { grid-column: 1 / -1; }
}

/* Tall half-page (300×600): stack every answer. */
@container (min-height: 480px) and (max-width: 420px) {
  .layout { padding: 28px 24px; }
  .tagline { margin-bottom: 16px; }
  .question { font-size: 20px; margin-bottom: 20px; }
  .choice { font-size: 15px; gap: 12px; }
  .dot { width: 20px; height: 20px; }
  .answers.is-pills {
    display: flex;
    flex-direction: column;
    grid-template-columns: none;
    gap: 10px;
  }
  .answers.is-pills .pill {
    width: 100%;
    padding: 14px 12px;
    font-size: 13px;
  }
  .answers.is-pills .pill.is-wide { grid-column: auto; }
}

/* Billboard 970×250: question left, answers right; Submit below answers. */
@container (min-width: 800px) and (min-height: 180px) and (max-height: 320px) {
  .layout {
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    padding: 24px 28px;
  }
  .copy {
    flex: 0 0 42%;
    padding-inline-end: 28px;
    border-inline-end: 1px solid var(--bls-line);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .tagline { margin-bottom: 12px; }
  .question { font-size: 22px; margin-bottom: 0; line-height: 1.25; }
  .panel {
    flex: 1;
    padding-inline-start: 28px;
    justify-content: center;
  }
  .answers {
    flex: 0 1 auto;
    justify-content: center;
  }
  .answers.is-pills {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    grid-template-columns: none;
    gap: 8px;
    align-content: stretch;
  }
  .answers.is-pills .pill {
    width: 100%;
    flex: 0 0 auto;
    padding: 10px 12px;
  }
  .answers.is-pills.is-dense {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .answers.is-pills.is-dense .pill {
    width: auto;
    flex: 1 1 calc(33.33% - 8px);
    min-width: calc(33.33% - 8px);
  }
  .answers.is-radios {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    align-content: center;
    gap: 10px 20px;
  }
  .answers.is-radios .choice { width: auto; font-size: 14px; }
  .submit {
    width: auto;
    min-width: 120px;
    max-width: 180px;
    margin-top: 12px;
    flex: 0 0 auto;
    align-self: flex-start;
    padding: 8px 20px;
    font-size: 12px;
    border-radius: 8px;
  }
  .rule { display: none; }
}

/* Leaderboard 728×90: 25% question, 50% answers, 25% Submit. */
@container (min-width: 560px) and (max-height: 140px) {
  .tagline { display: none; }
  .layout {
    flex-direction: row;
    align-items: center;
    padding: 8px 12px;
    gap: 0;
  }
  .copy {
    flex: 0 0 25%;
    max-width: 25%;
    padding: 0;
    padding-inline-end: 10px;
    border: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .question {
    font-size: 11px;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .panel {
    flex: 1 1 75%;
    flex-direction: row;
    align-items: center;
    min-height: 0;
    gap: 8px;
  }
  .answers {
    flex: 1 1 66.666%;
    padding: 0;
    gap: 6px;
    justify-content: center;
  }
  .answers.is-pills {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    grid-template-columns: none;
    align-content: center;
  }
  .answers.is-pills .pill {
    flex: 1 1 90px;
    min-width: 0;
    width: auto;
    padding: 6px 8px;
    font-size: 10px;
    border-radius: 8px;
  }
  .answers.is-radios {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    overflow: hidden;
    gap: 6px 10px;
  }
  .choice { font-size: 11px; gap: 6px; width: auto; }
  .dot { width: 14px; height: 14px; }
  .dot::after { width: 5px; height: 5px; }
  .submit {
    flex: 0 0 33.333%;
    width: auto;
    max-width: none;
    margin: 0;
    padding: 8px 10px;
    font-size: 11px;
    border-radius: 8px;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .rule { display: none; }
  .thanks-mark { width: 28px; height: 28px; }
  .thanks h2 { font-size: 12px; }
  .thanks p, .reset { display: none; }
}
`;

/**
 * @param {{
 *   question: string,
 *   answers: string[],
 *   brand?: string,
 *   clickToSubmit?: boolean,
 *   showReset?: boolean,
 *   optimized?: boolean,
 *   exposureFrequency?: number,
 *   metricId?: string,
 *   actionWord?: string,
 *   answerCount?: number,
 *   previewLocale?: string|null,
 * }} options
 */
export function buildSurveyHtml(options = {}) {
  const answers = (options.answers || []).filter((answer) => String(answer || '').trim());
  const questionHtml = highlightBrand(options.question, options.brand);
  const clickToSubmit = Boolean(options.clickToSubmit);
  const showReset = options.showReset !== false;
  const pills = !clickToSubmit;
  const oddLast = answers.length > 3 && answers.length % 2 === 1;
  const densePills = pills && answers.length > 4;
  const fewAnswers = answers.length <= 3;

  const answerMarkup = answers
    .map((answer, index) => {
      const label = escapeHtml(answer);
      const wide = oddLast && index === answers.length - 1 ? ' is-wide' : '';
      if (pills) {
        return `<button type="button" class="pill${wide}" data-index="${index}"><span class="bls-label">${label}</span></button>`;
      }
      return `<button type="button" class="choice${wide}" data-index="${index}"><span class="dot"></span><span class="bls-label">${label}</span></button>`;
    })
    .join('');

  const submitMarkup = clickToSubmit
    ? '<button type="button" class="submit" id="submit" disabled>Submit</button>'
    : '';
  const ruleMarkup = clickToSubmit ? '<hr class="rule" />' : '';
  const resetMarkup = showReset
    ? '<button type="button" class="reset" id="reset">Reset preview</button>'
    : '';
  const answersClass = [
    'answers',
    pills ? 'is-pills' : 'is-radios',
    densePills ? 'is-dense' : '',
    fewAnswers ? 'is-few' : 'is-fit',
  ].filter(Boolean).join(' ');
  const trackingJs = trackingRuntimeJs({
    answers,
    optimized: options.optimized,
    exposureFrequency: options.exposureFrequency,
  });
  const localeJs = surveyLocaleRuntime(buildSurveyPack({
    metricId: options.metricId,
    actionWord: options.actionWord,
    answerCount: options.answerCount,
    answers,
    brand: options.brand,
    question: options.question,
    previewLocale: options.previewLocale || null,
  }));

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Brand Lift Survey</title>
  <style>${SURVEY_CSS}</style>
</head>
<body>
  <div class="survey" id="survey">
    <div class="thanks" id="thanks">
      <div class="thanks-mark">${CHECK_SVG}</div>
      <h2 id="thanks-title">Thanks for your answer!</h2>
      ${resetMarkup}
    </div>
    <div class="layout" id="layout">
      <div class="copy">
        <p class="tagline" id="tagline">— One quick question</p>
        <p class="question" id="question">${questionHtml}</p>
      </div>
      ${ruleMarkup}
      <div class="panel">
      <div class="${answersClass}" id="answers">
        ${answerMarkup}
      </div>
      ${submitMarkup}
      </div>
    </div>
  </div>
  <script>
(function () {
  ${trackingJs}
  ${localeJs}
  var selected = null;
  var submitted = false;
  var layout = document.getElementById('layout');
  var thanks = document.getElementById('thanks');
  var submit = document.getElementById('submit');
  var clickToSubmit = ${clickToSubmit ? 'true' : 'false'};

  function mark(index) {
    selected = index;
    document.querySelectorAll('[data-index]').forEach(function (el) {
      el.classList.toggle('is-on', Number(el.getAttribute('data-index')) === index);
    });
    if (submit) {
      submit.disabled = false;
      submit.classList.add('is-ready');
    }
  }

  function submitAnswer(index) {
    if (submitted || index == null) return;
    submitted = true;
    selected = index;
    if (window.blsTrackAnswered) window.blsTrackAnswered(index);

    layout.classList.add('is-out');
    window.setTimeout(function () {
      layout.style.display = 'none';
      thanks.classList.add('is-in');
    }, 220);
  }

  document.getElementById('answers').addEventListener('click', function (event) {
    var button = event.target.closest('[data-index]');
    if (!button || submitted) return;
    var index = Number(button.getAttribute('data-index'));
    mark(index);
    if (!clickToSubmit) {
      window.setTimeout(function () { submitAnswer(index); }, 280);
    }
  });

  if (submit) {
    submit.addEventListener('click', function () {
      submitAnswer(selected);
    });
  }

  var reset = document.getElementById('reset');
  if (reset) {
    reset.addEventListener('click', function () {
      submitted = false;
      selected = null;
      thanks.classList.remove('is-in');
      layout.style.display = '';
      layout.classList.remove('is-out');
      document.querySelectorAll('[data-index]').forEach(function (el) {
        el.classList.remove('is-on');
      });
      if (submit) {
        submit.disabled = true;
        submit.classList.remove('is-ready');
      }
      if (window.blsTrackShown) window.blsTrackShown();
    });
  }

  if (window.blsTrackShown) window.blsTrackShown();
})();
  </script>
</body>
</html>`;
}

function stripTags(html) {
  return String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function decodeHtmlEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

export function looksLikeBuiltSurveyHtml(html) {
  const source = String(html || '');
  return /class="question"/.test(source) && /One quick question/.test(source);
}

/**
 * Recover question, answers, and click-to-submit from saved survey HTML.
 * Supports the current trafficked template and the older Keeper .opt markup.
 */
export function parseSurveyHtml(html) {
  const source = String(html || '');
  const parsed = {
    questionWording: '',
    answerOptions: [],
    clickToSubmit: false,
  };
  if (!source.trim()) return parsed;

  const answersMatch = source.match(/var BLS_ANSWERS = (\[[\s\S]*?\]);/);
  if (answersMatch) {
    try {
      const answers = JSON.parse(answersMatch[1]);
      if (Array.isArray(answers)) {
        parsed.answerOptions = answers.map((answer) => String(answer || '').trim()).filter(Boolean);
      }
    } catch (error) {
      // Fall through to markup parsing.
    }
  }

  parsed.clickToSubmit = /var clickToSubmit = true/.test(source)
    || /id="submit"/.test(source)
    || /id="sb"/.test(source);

  const questionMatch = source.match(/<p class="question"[^>]*>([\s\S]*?)<\/p>/i)
    || source.match(/%%QUESTION_TEXT%%/);
  if (questionMatch && questionMatch[1]) {
    parsed.questionWording = decodeHtmlEntities(stripTags(questionMatch[1]));
  }

  if (!parsed.answerOptions.length) {
    const indexed = [...source.matchAll(/data-index="\d+"[^>]*>([\s\S]*?)<\/button>/gi)];
    const legacy = [...source.matchAll(/<button class="opt"[^>]*>([\s\S]*?)<\/button>/gi)];
    const buttons = indexed.length ? indexed : legacy;
    parsed.answerOptions = buttons
      .map((match) => decodeHtmlEntities(stripTags(match[1])))
      .filter(Boolean);
  }

  return parsed;
}
